import { PORTAL_TABS, type PortalTab, type PortalViewOutput } from '@garage/shared';
import { createContext, useContext } from 'react';

/**
 * Navigation par onglets du portail (CONTRACT §18). L'onglet ouvert n'est **jamais** écrit dans l'adresse :
 * le fragment porte le jeton du client et ne doit pas changer (ni ancre `#…`, ni paramètre). Il est gardé en
 * mémoire de l'onglet du navigateur (`sessionStorage`), pour retrouver le même onglet après un rechargement.
 */
export type { PortalTab };

/** Onglets proposés : le compte rendu permanent (lecture seule, lien qui peut être transmis) n'a pas de messagerie. */
export function tabsFor(mode: PortalViewOutput['mode']): PortalTab[] {
  return mode === 'report' ? PORTAL_TABS.filter((t) => t !== 'messages') : [...PORTAL_TABS];
}

const TAB_STORAGE_KEY = 'portal.tab';

export function readStoredTab(allowed: readonly PortalTab[]): PortalTab {
  try {
    const raw = window.sessionStorage.getItem(TAB_STORAGE_KEY);
    if (raw && (allowed as readonly string[]).includes(raw)) return raw as PortalTab;
  } catch {
    // stockage indisponible (navigation privée, réglage) : premier onglet
  }
  return allowed[0] ?? 'summary';
}

export function storeTab(tab: PortalTab): void {
  try {
    window.sessionStorage.setItem(TAB_STORAGE_KEY, tab);
  } catch {
    // sans stockage, l'onglet n'est simplement pas retrouvé au rechargement
  }
}

/** Cible d'un lien interne : un onglet, et éventuellement un bloc de cet onglet (identifiant d'élément). */
export interface PortalNav {
  goTo: (tab: PortalTab, target?: string | null) => void;
}

export const PortalNavContext = createContext<PortalNav | null>(null);

export function usePortalNav(): PortalNav | null {
  return useContext(PortalNavContext);
}

/** Onglet qui contient un bloc du portail (boutons des cartes d'événement). */
export function tabOfTarget(target: string): PortalTab {
  if (target === 'fiche-reception') return 'reception';
  if (target === 'echanges' || target === 'ecrire') return 'messages';
  return 'work';
}

/**
 * Dernier message affiché (le plus récent) : la lecture enregistrée par `portal.markMessagesRead` s'arrête
 * à lui, pour qu'un message arrivé entre l'affichage et l'enregistrement reste non lu.
 */
export function latestMessageId(messages: PortalViewOutput['messages']): string | null {
  let last: PortalViewOutput['messages'][number] | null = null;
  for (const m of messages) if (!last || m.createdAt > last.createdAt) last = m;
  return last?.id ?? null;
}
